import React, { useState } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { UserPlus, Upload } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import GoogleSignInButton from '../components/GoogleSignInButton';
import ApiService from '../services/api';

const initialForm = {
  fullName: '',
  email: '',
  username: '',
  password: '',
  confirmPassword: '',
  wantContribute: false,
  bio: '',
  jobTitle: '',
  professionalExperience: '',
  specialization: '',
  linkedinUrl: '',
  avatarUrl: '',
  termsAiAccepted: false,
  termsPlagiarismAccepted: false,
  termsExclusiveAccepted: false
};

const Register = () => {
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { register, loginWithGoogle, user, isAdmin, isAuthor, isPendingContributor } = useAuth();
  const navigate = useNavigate();

  if (user) {
    if (isPendingContributor) return <Navigate to="/pending-approval" replace />;
    if (isAdmin || isAuthor) return <Navigate to="/admin" replace />;
    return <Navigate to="/" replace />;
  }

  const update = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const redirectAfterAuth = (authUser, pending, message) => {
    if (pending) {
      setInfo(message || 'Profile created and pending admin review.');
      navigate('/pending-approval');
      return;
    }
    if (authUser?.role === 'admin' || (authUser?.role === 'author' && authUser?.status === 'active')) {
      navigate('/admin');
      return;
    }
    navigate('/');
  };

  const validate = () => {
    if (form.password !== form.confirmPassword) {
      return 'Passwords do not match';
    }
    if (form.password.length < 6) {
      return 'Password must be at least 6 characters';
    }
    if (form.wantContribute) {
      if (!form.jobTitle.trim() || !form.professionalExperience.trim() || !form.bio.trim()) {
        return 'Contributors must provide job title, professional experience, and about info';
      }
      if (!form.termsAiAccepted || !form.termsPlagiarismAccepted || !form.termsExclusiveAccepted) {
        return 'You must accept all article publishing terms to contribute';
      }
    }
    return '';
  };

  const handleAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Upload after account exists is ideal; for registration store as data URL preview
    // and upload after register via optional second step. For simplicity, convert note:
    // We support URL field; local preview uses object URL until post-register upload.
    if (file.size > 2 * 1024 * 1024) {
      setError('Profile photo must be under 2MB');
      return;
    }
    setUploading(true);
    setError('');
    try {
      // Temporary local preview; actual upload happens after register if logged in
      const preview = URL.createObjectURL(file);
      setForm((prev) => ({ ...prev, avatarUrl: preview, _avatarFile: file }));
    } finally {
      setUploading(false);
    }
  };

  const uploadAvatarIfNeeded = async (file) => {
    if (!file) return null;
    try {
      const res = await ApiService.uploadAvatar(file);
      return res.avatarUrl;
    } catch (err) {
      console.warn('Avatar upload skipped:', err.message);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    const payload = {
      fullName: form.fullName.trim(),
      email: form.email.trim(),
      username: form.username.trim(),
      password: form.password,
      wantContribute: form.wantContribute,
      bio: form.bio.trim(),
      jobTitle: form.jobTitle.trim(),
      professionalExperience: form.professionalExperience.trim(),
      specialization: form.specialization.trim(),
      linkedinUrl: form.linkedinUrl.trim(),
      termsAiAccepted: form.termsAiAccepted,
      termsPlagiarismAccepted: form.termsPlagiarismAccepted,
      termsExclusiveAccepted: form.termsExclusiveAccepted
    };

    const result = await register(payload);
    if (!result.success) {
      setSubmitting(false);
      setError(result.error || 'Registration failed');
      return;
    }

    if (form._avatarFile) {
      const url = await uploadAvatarIfNeeded(form._avatarFile);
      if (url) {
        try {
          await ApiService.updateUser(result.user.id, { avatarUrl: url });
        } catch (err) {
          console.warn(err);
        }
      }
    }

    setSubmitting(false);
    redirectAfterAuth(result.user, result.pendingApproval, result.message);
  };

  const handleGoogle = async (credential) => {
    setError('');
    if (form.wantContribute) {
      const validationError = validate();
      // Password not needed for Google — only contributor fields
      if (form.wantContribute) {
        if (!form.jobTitle.trim() || !form.professionalExperience.trim() || !form.bio.trim()) {
          setError('Fill contributor details and accept terms before using Google Sign-In as a contributor');
          return;
        }
        if (!form.termsAiAccepted || !form.termsPlagiarismAccepted || !form.termsExclusiveAccepted) {
          setError('Accept all publishing terms before continuing');
          return;
        }
      }
      if (validationError && form.password) {
        // ignore password mismatch when using Google without password fields filled
      }
    }

    setSubmitting(true);
    const result = await loginWithGoogle({
      credential,
      wantContribute: form.wantContribute,
      bio: form.bio.trim(),
      jobTitle: form.jobTitle.trim(),
      professionalExperience: form.professionalExperience.trim(),
      specialization: form.specialization.trim(),
      linkedinUrl: form.linkedinUrl.trim(),
      termsAiAccepted: form.termsAiAccepted,
      termsPlagiarismAccepted: form.termsPlagiarismAccepted,
      termsExclusiveAccepted: form.termsExclusiveAccepted
    });
    setSubmitting(false);

    if (result.success) {
      redirectAfterAuth(result.user, result.pendingApproval);
    } else {
      setError(result.error || 'Google sign-in failed');
    }
  };

  return (
    <div className="login-container">
      <div className="login-card auth-card-wide register-card">
        <div style={{ textAlign: 'center', marginBottom: '12px' }}>
          <UserPlus size={44} color="#0f766e" />
        </div>
        <h2 className="login-title">Create account</h2>
        <p className="auth-subtitle">
          Join as a member to comment and leave feedback, or apply as a contributor to publish articles.
        </p>

        {error && <div className="auth-alert auth-alert-error">{error}</div>}
        {info && <div className="auth-alert auth-alert-info">{info}</div>}

        <form onSubmit={handleSubmit} className="register-form">
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Full name</label>
              <input name="fullName" className="form-input" value={form.fullName} onChange={update} required />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input type="email" name="email" className="form-input" value={form.email} onChange={update} required />
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Username (login)</label>
              <input name="username" className="form-input" value={form.username} onChange={update} required />
            </div>
            <div className="form-group">
              <label className="form-label">Profile photo</label>
              <label className="avatar-upload-btn">
                <Upload size={16} />
                {uploading ? 'Uploading…' : (form.avatarUrl ? 'Change photo' : 'Choose photo')}
                <input type="file" accept="image/*" onChange={handleAvatar} hidden />
              </label>
              {form.avatarUrl && (
                <img src={form.avatarUrl} alt="Preview" className="avatar-preview" />
              )}
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Password</label>
              <input type="password" name="password" className="form-input" value={form.password} onChange={update} required minLength={6} />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm password</label>
              <input type="password" name="confirmPassword" className="form-input" value={form.confirmPassword} onChange={update} required minLength={6} />
            </div>
          </div>

          <label className="checkbox-row contribute-toggle">
            <input
              type="checkbox"
              name="wantContribute"
              checked={form.wantContribute}
              onChange={update}
            />
            <span>
              <strong>I want to contribute</strong> — apply as an article contributor (requires admin approval)
            </span>
          </label>

          {form.wantContribute && (
            <div className="contributor-fields">
              <h3 className="contributor-section-title">Contributor profile</h3>
              <div className="form-group">
                <label className="form-label">Job title</label>
                <input name="jobTitle" className="form-input" value={form.jobTitle} onChange={update} placeholder="e.g. Senior SQL Server DBA" required={form.wantContribute} />
              </div>
              <div className="form-group">
                <label className="form-label">About / professional experience</label>
                <textarea
                  name="professionalExperience"
                  className="form-input form-textarea"
                  value={form.professionalExperience}
                  onChange={update}
                  placeholder="Summarize your DBA experience, specialties, and background"
                  required={form.wantContribute}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Short bio (shown on author page)</label>
                <textarea name="bio" className="form-input form-textarea" value={form.bio} onChange={update} required={form.wantContribute} />
              </div>
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Specialization</label>
                  <input name="specialization" className="form-input" value={form.specialization} onChange={update} placeholder="Performance, HA, Azure SQL…" />
                </div>
                <div className="form-group">
                  <label className="form-label">LinkedIn URL</label>
                  <input type="url" name="linkedinUrl" className="form-input" value={form.linkedinUrl} onChange={update} placeholder="https://linkedin.com/in/…" />
                </div>
              </div>

              <h3 className="contributor-section-title">Article publishing terms</h3>
              <div className="terms-box">
                <label className="checkbox-row">
                  <input type="checkbox" name="termsAiAccepted" checked={form.termsAiAccepted} onChange={update} />
                  <span>
                    <strong>AI Generated Content Prohibited</strong><br />
                    I understand that AI Generated content is strictly prohibited.
                  </span>
                </label>
                <label className="checkbox-row">
                  <input type="checkbox" name="termsPlagiarismAccepted" checked={form.termsPlagiarismAccepted} onChange={update} />
                  <span>
                    <strong>Plagiarized Content Prohibited</strong><br />
                    I understand that plagiarized content is strictly prohibited and there is a zero tolerance policy for plagiarized content.
                  </span>
                </label>
                <label className="checkbox-row">
                  <input type="checkbox" name="termsExclusiveAccepted" checked={form.termsExclusiveAccepted} onChange={update} />
                  <span>
                    <strong>Content Exclusive to MSSQLDBATeam (Required)</strong><br />
                    I understand that my submitted content is exclusive to MSSQLDBATeam.com and will not be published on any other sites.
                  </span>
                </label>
              </div>
            </div>
          )}

          <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={submitting}>
            <UserPlus size={16} />
            {submitting ? 'Creating account…' : (form.wantContribute ? 'Submit contributor application' : 'Create account')}
          </button>
        </form>

        <div className="auth-divider"><span>or continue with</span></div>
        <p className="auth-google-note">
          {form.wantContribute
            ? 'Fill contributor details and terms above, then use Google to apply.'
            : 'Create a regular member account with your Google email.'}
        </p>
        <GoogleSignInButton onCredential={handleGoogle} disabled={submitting} text="signup_with" />

        <p className="auth-footer-text">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
